import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { applyUpdate, onUpdateAvailable } from '../pwa/registerServiceWorker';
import { useAppStore } from '../state/store';
import { Button, Sheet } from '../ui/components';

/**
 * Offers a new version rather than swapping code underneath the player.
 *
 * The offer waits until play has stopped. Interrupting someone mid-puzzle with a modal that
 * covers the board is the wrong moment to ask, and a waiting service worker is in no hurry:
 * it stays waiting until the level ends, is paused, or the player returns to a menu.
 */
export function UpdatePrompt() {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const { levelPhase, paused, persistInterrupted } = useAppStore(
    useShallow((state) => ({
      levelPhase: state.levelPhase,
      paused: state.paused,
      persistInterrupted: state.persistInterrupted,
    })),
  );

  useEffect(() => onUpdateAvailable(setRegistration), []);

  const midLevel = levelPhase === 'ready' && !paused;
  if (registration === null || midLevel) return null;

  return (
    <Sheet title="A new version is ready" labelledBy="update-title">
      <p className="ar-muted">
        Reloading takes a moment. Your progress and the level you are on are saved first.
      </p>
      <div className="ar-stack" style={{ marginTop: 'var(--ar-space-4)' }}>
        <Button
          variant="primary"
          size="block"
          onClick={() => {
            persistInterrupted();
            applyUpdate(registration);
          }}
        >
          Reload now
        </Button>
        <Button size="block" variant="ghost" onClick={() => setRegistration(null)}>
          Later
        </Button>
      </div>
    </Sheet>
  );
}
